'use client';
import { useEffect } from 'react';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';
import ServicesInteractive from './components/ServicesInteractive';
import { analytics } from '@/lib/analytics';

export default function ServicesOverviewPage() {
  // Track service overview page view
  useEffect(() => {
    analytics.trackServiceView('Services Overview');
  }, []);

  const services = [
  {
    id: 'window-cleaning',
    title: 'Window Cleaning',
    description: 'Crystal-clear windows that enhance your home\'s appearance and let natural light shine through. Professional interior and exterior window cleaning with streak-free results.',
    image: "/assets/images/image-1769857188223.png",
    alt: 'Crystal Clean professional worker using water-fed pole system to clean residential windows',
    benefits: [
    'Improves natural light and home brightness',
    'Enhances curb appeal and property value',
    'Removes hard water stains and mineral deposits',
    'Extends window lifespan by preventing damage',
    'Provides clear views and better visibility'],
    frequency: 'Quarterly or bi-annually',
    icon: 'WindowIcon',
    features: {
      duration: '3-5 hours',
      frequency: 'Quarterly',
      difficulty: 'Moderate',
      season: 'Year-round'
    },
    pricingTiers: [
    { size: 'Small homes (up to 1,500 sq ft)', price: 'Starting at $180' },
    { size: 'Medium homes (1,500–2,500 sq ft)', price: '$220–$300' },
    { size: 'Large homes (2,500–3,500 sq ft)', price: '$300–$420' },
    { size: 'Luxury / custom homes (3,500+ sq ft)', price: '$450+' }],

    minimumPrice: 180,
    averageProject: '$280–$350'
  },
  {
    id: 'power-washing',
    title: 'Power Washing',
    description: 'Professional power washing for driveways, patios, walkways, and more. High-pressure cleaning removes dirt, grime, and stains to restore surfaces to like-new condition.',
    image: "/assets/images/image-1769885847287.png",
    alt: 'Crystal Clean worker power washing concrete surface with professional equipment',
    benefits: [
    'Removes stubborn dirt, oil, and grime',
    'Eliminates mold, mildew, and algae buildup',
    'Restores original surface appearance',
    'Increases property value and curb appeal',
    'Prepares surfaces for sealing or painting'],
    frequency: 'Annually',
    icon: 'SparklesIcon',
    features: {
      duration: '3-6 hours',
      frequency: 'Annually',
      difficulty: 'Moderate',
      season: 'Spring & Summer'
    },
    startingPrice: 150,
    averageProject: '$220–$300'
  },
  {
    id: 'roof-cleaning',
    title: 'Roof Cleaning (Soft Wash)',
    description: 'Gentle yet effective roof cleaning that removes algae, moss, and debris without damaging shingles. Protect your investment and maintain your home\'s appearance.',
    image: "/assets/images/image-1769887074519.png",
    alt: 'Professional roof cleaning specialist using soft-wash system to remove moss and algae from residential shingles',
    benefits: [
    'Removes damaging algae and moss growth',
    'Prevents premature roof deterioration',
    'Improves energy efficiency by reflecting heat',
    'Maintains manufacturer warranty requirements',
    'Enhances overall home appearance and value'],
    frequency: 'Every 2-3 years',
    icon: 'HomeIcon',
    features: {
      duration: '4-6 hours',
      frequency: 'Every 2-3 years',
      difficulty: 'Complex',
      season: 'Spring & Summer'
    },
    startingPrice: 300,
    averageProject: '$450–$600'
  },
  {
    id: 'gutter-cleaning',
    title: 'Gutter Cleaning',
    description: 'Protect your home from water damage with thorough gutter cleaning that removes debris, prevents clogs, and ensures proper drainage throughout the year.',
    image: "/assets/images/image-1769864300298.png",
    alt: 'Crystal Clean professional team member performing gutter cleaning service on residential home',
    benefits: [
    'Prevents water damage to foundation and landscaping',
    'Eliminates pest nesting areas in clogged gutters',
    'Extends the lifespan of your gutter system',
    'Reduces risk of basement flooding and mold growth',
    'Maintains proper water flow away from your home'],
    frequency: 'Twice yearly (Spring & Fall)',
    icon: 'HomeModernIcon',
    features: {
      duration: '2-4 hours',
      frequency: 'Twice yearly',
      difficulty: 'Moderate',
      season: 'Spring & Fall'
    },
    startingPrice: 150,
    averageProject: '$200–$275'
  },
  {
    id: 'house-washing',
    title: 'House Washing (Exterior Siding)',
    description: 'Complete exterior cleaning for siding and home surfaces. Our soft-wash technique safely removes dirt, mildew, and stains without causing damage.',
    image: "/assets/images/image-1769856829847.png",
    alt: 'Professional using soft-wash technique to clean home siding and exterior surfaces',
    benefits: [
    'Removes dirt, mildew, and environmental stains',
    'Prevents siding deterioration and discoloration',
    'Increases home value and curb appeal',
    'Prepares surfaces for painting or staining',
    'Creates healthier living environment'],
    frequency: 'Annually',
    icon: 'HomeIcon',
    features: {
      duration: '4-8 hours',
      frequency: 'Annually',
      difficulty: 'Moderate',
      season: 'Spring & Summer'
    },
    startingPrice: 200,
    averageProject: '$300–$400'
  },
  {
    id: 'christmas-lights',
    title: 'Christmas Light Installation',
    description: 'Professional holiday lighting installation and removal service. We handle everything from design to installation, maintenance, and post-season takedown. Lights supplied by Crystal Clean.',
    image: "/assets/images/image-1769885972079.png",
    alt: 'Crystal Clean professional worker installing colorful Christmas lights on residential home',
    benefits: [
    'Professional design and installation',
    'Commercial-grade LED lights included',
    'Maintenance throughout the season',
    'Safe removal and storage after holidays',
    'No ladder climbing or safety risks'],
    frequency: 'Seasonal (November - January)',
    icon: 'SparklesIcon',
    features: {
      duration: '4-8 hours',
      frequency: 'Seasonal',
      difficulty: 'Complex',
      season: 'Winter'
    },
    startingPrice: 450,
    averageProject: '$750–$1,200'
  }];


  const galleryItems = [
  {
    id: '1',
    service: 'Gutter Cleaning',
    before: {
      image: "https://images.unsplash.com/photo-1665599218809-670be8fc3b3d",
      alt: 'Clogged rain gutter filled with brown leaves and debris overflowing onto roof'
    },
    after: {
      image: "https://images.unsplash.com/photo-1682687208661-38b9f796f8f9",
      alt: 'Spotless white rain gutter system with clear downspouts and no debris'
    },
    customerName: 'Sarah Mitchell',
    location: 'Oak Park'
  },
  {
    id: '2',
    service: 'Window Cleaning',
    before: {
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_12ea953f7-1765086838033.png",
      alt: 'Dirty residential windows with water spots and streaks blocking clear view'
    },
    after: {
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1acc9d750-1769645914349.png",
      alt: 'Crystal clear residential windows with perfect transparency and no streaks'
    },
    customerName: 'Michael Chen',
    location: 'Riverside'
  },
  {
    id: '3',
    service: 'Roof Cleaning',
    before: {
      image: "https://images.unsplash.com/photo-1602586179661-bb6b912de5be",
      alt: 'Residential roof with dark algae stains and moss growth on shingles'
    },
    after: {
      image: "https://images.unsplash.com/photo-1707897283683-00a5b942a167",
      alt: 'Clean residential roof with uniform dark shingles free of algae and moss'
    },
    customerName: 'Jennifer Rodriguez',
    location: 'Maple Grove'
  },
  {
    id: '4',
    service: 'Exterior Washing',
    before: {
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_101e97e9a-1764675966785.png",
      alt: 'Dirty white vinyl siding with green mildew stains and dirt buildup'
    },
    after: {
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_18937dce8-1765284254562.png",
      alt: 'Pristine white vinyl siding on home exterior after professional cleaning'
    },
    customerName: 'David Thompson',
    location: 'Lakeside'
  },
  {
    id: '5',
    service: 'Gutter Cleaning',
    before: {
      image: "https://images.unsplash.com/photo-1665599218809-670be8fc3b3d",
      alt: 'Overflowing gutter with leaves and debris causing water damage to fascia'
    },
    after: {
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_12cdb0de8-1769645913092.png",
      alt: 'Clean white gutters with proper water flow and no blockages'
    },
    customerName: 'Emily Watson',
    location: 'Hillcrest'
  },
  {
    id: '6',
    service: 'Window Cleaning',
    before: {
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_17852d1a8-1769888266068.png",
      alt: 'Foggy windows with hard water stains and mineral deposits'
    },
    after: {
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1acc9d750-1769645914349.png",
      alt: 'Sparkling clean windows with perfect clarity and no water spots'
    },
    customerName: 'Robert Anderson',
    location: 'Sunset Valley'
  }];


  const seasons = [
  {
    name: 'Spring',
    months: 'March - May',
    icon: 'SunIcon',
    color: 'green',
    recommendations: [
    {
      service: 'Gutter Cleaning',
      priority: 'High' as const,
      reason: 'Remove winter debris and prepare for spring rains to prevent water damage and ensure proper drainage.'
    },
    {
      service: 'Window Cleaning',
      priority: 'High' as const,
      reason: 'Clear away winter grime and pollen buildup to maximize natural light as days get longer.'
    },
    {
      service: 'Exterior Washing',
      priority: 'Medium' as const,
      reason: 'Remove winter salt, dirt, and mildew before summer heat sets in and stains become harder to remove.'
    }]

  },
  {
    name: 'Summer',
    months: 'June - August',
    icon: 'FireIcon',
    color: 'yellow',
    recommendations: [
    {
      service: 'Roof Cleaning',
      priority: 'High' as const,
      reason: 'Ideal weather conditions for safe roof cleaning. Remove algae and moss before they cause damage.'
    },
    {
      service: 'Exterior Washing',
      priority: 'High' as const,
      reason: 'Perfect time for comprehensive exterior cleaning. Surfaces dry quickly and results last longer.'
    },
    {
      service: 'Window Cleaning',
      priority: 'Low' as const,
      reason: 'Optional mid-year cleaning if you notice buildup from summer storms or pollen.'
    }]

  },
  {
    name: 'Fall',
    months: 'September - November',
    icon: 'SparklesIcon',
    color: 'orange',
    recommendations: [
    {
      service: 'Gutter Cleaning',
      priority: 'High' as const,
      reason: 'Critical service to remove fallen leaves before winter. Prevents ice dams and water damage.'
    },
    {
      service: 'Window Cleaning',
      priority: 'Medium' as const,
      reason: 'Prepare for winter by cleaning windows before cold weather makes the job more difficult.'
    },
    {
      service: 'Roof Cleaning',
      priority: 'Low' as const,
      reason: 'Last chance for roof maintenance before winter. Address any moss or algae issues now.'
    }]

  },
  {
    name: 'Winter',
    months: 'December - February',
    icon: 'CloudIcon',
    color: 'blue',
    recommendations: [
    {
      service: 'Gutter Cleaning',
      priority: 'Medium' as const,
      reason: 'Emergency cleaning if ice dams form or gutters become blocked during winter storms.'
    },
    {
      service: 'Window Cleaning',
      priority: 'Low' as const,
      reason: 'Interior window cleaning only during winter months. Exterior cleaning resumes in spring.'
    },
    {
      service: 'Planning',
      priority: 'High' as const,
      reason: 'Perfect time to schedule spring services and take advantage of early booking discounts.'
    }]

  }];


  const serviceOptions = [
  { id: 'gutter-cleaning', name: 'Gutter Cleaning', basePrice: 200 },
  { id: 'window-cleaning', name: 'Window Cleaning', basePrice: 150 },
  { id: 'roof-cleaning', name: 'Roof Cleaning', basePrice: 600 },
  { id: 'powerwashing', name: 'Powerwashing', basePrice: 250 },
  { id: 'christmas-lights', name: 'Christmas Lights Installation', basePrice: 400 },
  { id: 'exterior-washing', name: 'Exterior Washing', basePrice: 350 }];


  return (
    <>
      <Header />
      <main className="min-h-screen bg-background pt-20">
        <section className="bg-primary text-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="font-serif text-4xl md:text-5xl font-semibold mb-6">
                Professional Exterior Cleaning Services
              </h1>
              <p className="text-lg opacity-90 leading-relaxed mb-8">
                Comprehensive home care solutions designed to protect your investment and maintain your property's beauty. Each service is performed with meticulous attention to detail and respect for your home.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link
                  href="/get-quote"
                  className="px-8 py-3 bg-green-600 text-white font-semibold rounded-md hover:bg-green-700 transition-smooth shadow-brand">

                  Get Free Quote
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-surface">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                <div className="bg-card rounded-lg p-6 text-center shadow-brand">
                  <Icon name="ShieldCheckIcon" size={40} className="text-success mx-auto mb-4" />
                  <h3 className="font-semibold text-foreground mb-2">Fully Insured</h3>
                  <p className="text-sm text-muted-foreground">
                    Complete liability and workers' compensation coverage for your peace of mind
                  </p>
                </div>
                <div className="bg-card rounded-lg p-6 text-center shadow-brand">
                  <Icon name="ClockIcon" size={40} className="text-primary mx-auto mb-4" />
                  <h3 className="font-semibold text-foreground mb-2">5+ Years Experience</h3>
                  <p className="text-sm text-muted-foreground">
                    Trusted by thousands of homeowners for reliable, quality service
                  </p>
                </div>
                <div className="bg-card rounded-lg p-6 text-center shadow-brand">
                  <Icon name="CheckBadgeIcon" size={40} className="text-warning mx-auto mb-4" />
                  <h3 className="font-semibold text-foreground mb-2">Satisfaction Guaranteed</h3>
                  <p className="text-sm text-muted-foreground">
                    We stand behind our work with a comprehensive service guarantee
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="font-serif text-3xl md:text-4xl font-semibold text-foreground mb-4">
                  Our Core Services
                </h2>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Each service is designed to protect your home and enhance its appearance. We use proven techniques and quality equipment to deliver consistent, reliable results.
                </p>
              </div>

              <ServicesInteractive
                services={services}
                galleryItems={galleryItems}
                seasons={seasons}
                serviceOptions={serviceOptions} />

            </div>
          </div>
        </section>

        <section className="py-16 bg-accent">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="font-serif text-3xl font-semibold text-foreground mb-6">
                Ready to Protect Your Investment?
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Schedule your free consultation today and discover why homeowners trust CrystalClean for their exterior maintenance needs.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Link
                  href="/get-quote"
                  className="px-8 py-4 bg-green-600 text-white font-semibold rounded-md hover:bg-green-700 transition-smooth shadow-brand inline-flex items-center justify-center">

                  <Icon name="DocumentTextIcon" size={20} className="mr-2" />
                  Request Free Quote
                </Link>
                <Link
                  href="/customer-reviews"
                  className="px-8 py-4 bg-card text-foreground font-semibold rounded-md hover:bg-muted transition-smooth shadow-brand inline-flex items-center justify-center">

                  <Icon name="StarIcon" size={20} className="mr-2" />
                  Read Customer Reviews
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>);

}